import type { LegalCaseFields } from '../types/legalCase';
// Client Vault is the sole CRM storage backend. Google Drive support was
// removed so client-side code cannot export sensitive CRM data in plaintext.

export interface CRMClient {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  user_email: string;
  assigned_to_email?: string | null;
  client_type?: string;
  prenume?: string | null;
  cnp?: string | null;
  data_nasterii?: string | null;
  loc_nasterii?: string | null;
  cetatenie?: string | null;
  denumire_firma?: string | null;
  cui?: string | null;
  nr_reg_com?: string | null;
  reprezentant_legal?: string | null;
  capital_social?: string | null;
  adresa?: string | null;
  sediu_social?: string | null;
  judet?: string | null;
  oras?: string | null;
  cod_postal?: string | null;
  telefon_secundar?: string | null;
  fax?: string | null;
  website?: string | null;
  comentariu?: string | null;
  status?: string;
  sursa?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface CRMDocument {
  id: number | string;
  client_id: number;
  filename: string;
  upload_date: string;
  user_email: string;
  size?: number | null;
  mime_type?: string | null;
}

export interface Folder {
  id: number;
  nume: string;
  prompt_user_request?: string | null;
  prompt_fact_situation?: string | null;
  created_at: string;
  user_id: string;
}

export interface CaseData extends LegalCaseFields {
  id: number | string;
  vault_item_id?: number;
  data: Record<string, unknown>;
  folder_id?: number | null;
}

export interface CRMCalendarEvent {
  id: number;
  title: string;
  description?: string;
  event_date: string;
  event_time?: string | null;
  location?: string | null;
  invitee_emails?: string | null;
  creator_email: string;
  client_id?: number | null;
  send_email_reminder?: boolean;
  email_reminder_type?: string;
  gcal_event_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface CRMTask {
  id: number;
  title: string;
  description?: string;
  status: string;
  priority: string;
  creator_email: string;
  assignee_email: string;
  due_date?: string | null;
  client_id?: number | null;
  send_email_reminder?: boolean;
  email_reminder_type?: string;
  materie?: string | null;
  obiect?: string | null;
  gcal_event_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

async function parseError(response: Response, fallback: string): Promise<Error> {
  try {
    const payload = await response.json();
    return new Error(payload.detail || fallback);
  } catch {
    return new Error(fallback);
  }
}

class CRMStorageService {
  public getStorageMode(): 'server' {
    return 'server';
  }

  public async setStorageMode(mode: 'server' | 'google_drive'): Promise<void> {
    if (mode !== 'server') {
      throw new Error('Datele CRM pot fi stocate numai în Client Vault-ul administrat de server.');
    }
    const token = localStorage.getItem('token');
    const response = await fetch('/api/auth/update-storage-option', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
      body: JSON.stringify({ crm_storage_option: 'server' }),
    });
    if (!response.ok) throw await parseError(response, 'Nu s-a putut actualiza opțiunea de stocare.');
    localStorage.removeItem('crm_storage_option');
  }

  public async getClients(): Promise<CRMClient[]> {
    const response = await fetch('/api/crm/clients', { credentials: 'include' });
    if (!response.ok) throw await parseError(response, 'Nu s-au putut încărca clienții.');
    return response.json();
  }

  public async createClient(client: Partial<CRMClient>): Promise<CRMClient> {
    const response = await fetch('/api/crm/clients', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(client),
    });
    if (!response.ok) throw await parseError(response, 'Nu s-a putut crea clientul.');
    return response.json();
  }

  public async updateClient(clientId: number, client: Partial<CRMClient>): Promise<CRMClient> {
    const response = await fetch(`/api/crm/clients/${clientId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(client),
    });
    if (!response.ok) throw await parseError(response, 'Nu s-a putut edita clientul.');
    return response.json();
  }

  public async deleteClient(clientId: number): Promise<void> {
    const response = await fetch(`/api/crm/clients/${clientId}`, { method: 'DELETE', credentials: 'include' });
    if (!response.ok) throw await parseError(response, 'Nu s-a putut șterge clientul.');
  }

  public async getClientDocuments(clientId: number): Promise<CRMDocument[]> {
    const response = await fetch(`/api/crm/clients/${clientId}/documents`, { credentials: 'include' });
    if (!response.ok) throw await parseError(response, 'Nu s-au putut încărca documentele.');
    return response.json();
  }

  public async uploadDocument(clientId: number, blob: Blob, fileName: string): Promise<CRMDocument> {
    const formData = new FormData();
    formData.append('file', blob, fileName);
    const response = await fetch(`/api/crm/clients/${clientId}/documents`, {
      method: 'POST', body: formData, credentials: 'include',
    });
    if (!response.ok) throw await parseError(response, 'Încărcarea documentului a eșuat.');
    return response.json();
  }

  public async deleteDocument(documentId: number | string): Promise<void> {
    const response = await fetch(`/api/crm/documents/${documentId}`, { method: 'DELETE', credentials: 'include' });
    if (!response.ok) throw await parseError(response, 'Ștergerea documentului a eșuat.');
  }

  public async downloadDocument(documentId: number | string, filename: string): Promise<void> {
    const response = await fetch(`/api/crm/documents/${documentId}/download`, { credentials: 'include' });
    if (!response.ok) throw await parseError(response, 'Descărcarea documentului a eșuat.');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.rel = 'noopener';
      link.click();
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  public async getFolders(): Promise<Folder[]> {
    const response = await fetch('/api/dosar/folders', { credentials: 'include' });
    if (!response.ok) throw await parseError(response, 'Nu s-au putut încărca dosarele.');
    return response.json();
  }

  public async createFolder(name: string): Promise<Folder> {
    const response = await fetch('/api/dosar/folders', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ nume: name }),
    });
    if (!response.ok) throw await parseError(response, 'Nu s-a putut crea dosarul.');
    return response.json();
  }

  public async updateFolder(folderId: number, data: { nume?: string; prompt_user_request?: string; prompt_fact_situation?: string }): Promise<Folder> {
    const response = await fetch(`/api/dosar/folders/${folderId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(data),
    });
    if (!response.ok) throw await parseError(response, 'Nu s-a putut actualiza dosarul.');
    return response.json();
  }

  public async deleteFolder(folderId: number): Promise<void> {
    const response = await fetch(`/api/dosar/folders/${folderId}`, { method: 'DELETE', credentials: 'include' });
    if (!response.ok) throw await parseError(response, 'Nu s-a putut șterge dosarul.');
  }

  public async getDosarItems(folderId?: number | null): Promise<CaseData[]> {
    const query = folderId !== undefined && folderId !== null ? `?folder_id=${folderId}` : '';
    const response = await fetch(`/api/dosar/${query}`, { credentials: 'include' });
    if (!response.ok) throw await parseError(response, 'Nu s-au putut încărca spețele salvate.');
    const data = await response.json();
    return data.map((item: { id: number; case_id: number | string; case_data: Record<string, unknown>; folder_id?: number | null }) => ({
      id: item.case_id, vault_item_id: item.id, data: item.case_data, folder_id: item.folder_id,
    }));
  }

  public async addDosarItem(item: Record<string, unknown>, folderId?: number | null): Promise<CaseData> {
    const payload = { ...item, ...(folderId ? { folder_id: folderId } : {}) };
    const response = await fetch('/api/dosar/', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(payload),
    });
    if (!response.ok) throw await parseError(response, 'Nu s-a putut salva speța.');
    const data = await response.json();
    return { id: data.case_id, vault_item_id: data.id, data: data.case_data, folder_id: data.folder_id };
  }

  public async removeDosarItem(vaultItemId: number): Promise<void> {
    const response = await fetch(`/api/dosar/items/${vaultItemId}`, { method: 'DELETE', credentials: 'include' });
    if (!response.ok) throw await parseError(response, 'Ștergerea speței din dosar a eșuat.');
  }

  public async getTasks(): Promise<CRMTask[]> {
    const response = await fetch('/api/crm/tasks', { credentials: 'include' });
    if (!response.ok) throw await parseError(response, 'Nu s-au putut încărca sarcinile.');
    return response.json();
  }

  public async createTask(task: Partial<CRMTask>): Promise<CRMTask> {
    const response = await fetch('/api/crm/tasks', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(task),
    });
    if (!response.ok) throw await parseError(response, 'Nu s-a putut crea sarcina.');
    return response.json();
  }

  public async updateTask(taskId: number | string, task: Partial<CRMTask>): Promise<CRMTask> {
    const response = await fetch(`/api/crm/tasks/${taskId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(task),
    });
    if (!response.ok) throw await parseError(response, 'Nu s-a putut edita sarcina.');
    return response.json();
  }

  public async deleteTask(taskId: number | string): Promise<void> {
    const response = await fetch(`/api/crm/tasks/${taskId}`, { method: 'DELETE', credentials: 'include' });
    if (!response.ok) throw await parseError(response, 'Ștergerea sarcinii a eșuat.');
  }

  public async getCalendarEvents(): Promise<CRMCalendarEvent[]> {
    const response = await fetch('/api/crm/calendar/events', { credentials: 'include' });
    if (!response.ok) throw await parseError(response, 'Nu s-au putut încărca evenimentele.');
    return response.json();
  }

  public async createCalendarEvent(event: Partial<CRMCalendarEvent>): Promise<CRMCalendarEvent> {
    const response = await fetch('/api/crm/calendar/events', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(event),
    });
    if (!response.ok) throw await parseError(response, 'Nu s-a putut crea evenimentul.');
    return response.json();
  }

  public async updateCalendarEvent(eventId: number | string, event: Partial<CRMCalendarEvent>): Promise<CRMCalendarEvent> {
    const response = await fetch(`/api/crm/calendar/events/${eventId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(event),
    });
    if (!response.ok) throw await parseError(response, 'Nu s-a putut edita evenimentul.');
    return response.json();
  }

  public async deleteCalendarEvent(eventId: number | string): Promise<void> {
    const response = await fetch(`/api/crm/calendar/events/${eventId}`, { method: 'DELETE', credentials: 'include' });
    if (!response.ok) throw await parseError(response, 'Ștergerea evenimentului a eșuat.');
  }

  public async resetStorageToServer(): Promise<void> {
    await this.setStorageMode('server');
  }
}

export const crmStorageService = new CRMStorageService();
